<template>
  <div class="inv-page inv-page-detail">

    <!-- Back + actions bar -->
    <div class="inv-back-bar">
      <button class="btn btn-sm inv-btn-ghost" @click="$router.back()">
        <i class="bi bi-arrow-left mr-1"></i> Back to Stock Cards
      </button>
      <div v-if="card" class="d-flex" style="gap:8px">
        <button class="btn btn-sm inv-btn-ghost" @click="openEdit">
          <i class="bi bi-pencil mr-1"></i> Edit
        </button>
        <button class="btn btn-sm inv-btn-soft" @click="openReceive">
          <i class="bi bi-box-arrow-in-down mr-1"></i> Receive Stock
        </button>
        <button class="btn btn-sm inv-btn-ghost" @click="printCard">
          <i class="bi bi-printer mr-1"></i> Print Appendix 58
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5 text-muted">
      <span class="spinner-border spinner-border-sm mr-2"></span> Loading stock card…
    </div>

    <div v-else-if="loadError" class="inv-error-state">
      <i class="bi bi-exclamation-triangle inv-error-icon"></i>
      <p class="mb-1 font-weight-bold">Could not load stock card</p>
      <p class="text-muted small mb-3">{{ loadError }}</p>
      <button class="btn btn-sm inv-btn-primary" @click="load">Try again</button>
    </div>

    <template v-else-if="card">
      <!-- Header card -->
      <div class="inv-card mb-3">
        <div class="inv-card-body">
          <div class="d-flex justify-content-between align-items-start flex-wrap" style="gap:12px">
            <div>
              <div class="d-flex align-items-center mb-1" style="gap:8px">
                <span class="inv-code-chip">{{ card.stock_number }}</span>
                <span v-if="card.fund_cluster" class="inv-status inv-status-cancelled">
                  Fund {{ card.fund_cluster }}
                </span>
                <span class="inv-status" :class="card.is_low_stock ? 'inv-status-low' : 'inv-status-ok'">
                  {{ card.is_low_stock ? '⚠ Low Stock' : '✓ OK' }}
                </span>
              </div>
              <h4 class="font-weight-bold mb-0">{{ card.item_description }}</h4>
              <p class="text-muted small mb-0">
                Unit of measure: <strong>{{ card.unit_of_measure }}</strong>
                <span v-if="card.category"> · {{ card.category }}</span>
              </p>
            </div>
            <div class="text-right text-muted small">
              <div>Reorder point: <strong>{{ n(card.reorder_point) }}</strong></div>
              <div>Unit cost: <strong>{{ peso(card.unit_cost) }}</strong></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Stats row -->
      <div class="inv-stats">
        <div class="inv-stat-card" :class="card.is_low_stock ? 'inv-stat-danger' : 'inv-stat-success'">
          <div class="inv-stat-label">Current Balance</div>
          <div class="inv-stat-value">{{ n(card.balance) }}</div>
          <div class="inv-stat-sub">{{ card.unit_of_measure }}</div>
        </div>
        <div class="inv-stat-card inv-stat-primary">
          <div class="inv-stat-label">Total Received</div>
          <div class="inv-stat-value">{{ n(totalReceived) }}</div>
          <div class="inv-stat-sub">{{ receiptCount }} transaction{{ receiptCount !== 1 ? 's' : '' }}</div>
        </div>
        <div class="inv-stat-card inv-stat-warning">
          <div class="inv-stat-label">Total Issued</div>
          <div class="inv-stat-value">{{ n(totalIssued) }}</div>
          <div class="inv-stat-sub">{{ issueCount }} transaction{{ issueCount !== 1 ? 's' : '' }}</div>
        </div>
        <div class="inv-stat-card inv-stat-neutral">
          <div class="inv-stat-label">Total Value</div>
          <div class="inv-stat-value">{{ peso(card.balance * card.unit_cost) }}</div>
          <div class="inv-stat-sub">at {{ peso(card.unit_cost) }} / {{ card.unit_of_measure }}</div>
        </div>
      </div>

      <!-- Transaction history -->
      <div class="inv-card">
        <div class="inv-card-body">
          <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap" style="gap:8px">
            <h5 class="font-weight-bold mb-0">Transaction History</h5>
            <div class="inv-filter-group">
              <button
                v-for="f in ['all','receipt','issue']" :key="f"
                type="button"
                class="inv-filter-btn"
                :class="{ active: filter === f }"
                @click="filter = f"
              >{{ { all:'All', receipt:'Receipts', issue:'Issues' }[f] }}</button>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table table-sm table-hover inv-table mb-0">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Reference</th>
                  <th>Supplier / Office</th>
                  <th class="text-right">Qty</th>
                  <th class="text-right">Balance After</th>
                  <th>Recorded By</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!filteredEntries.length">
                  <td colspan="7" class="inv-empty">No transactions yet.</td>
                </tr>
                <tr v-for="e in filteredEntries" :key="e.id">
                  <td class="text-nowrap">{{ fmtDate(e.transaction_date) }}</td>
                  <td>
                    <span class="inv-status" :class="e.type === 'receipt' ? 'inv-status-ok' : 'inv-status-returned'">
                      {{ e.type === 'receipt' ? 'Receipt' : 'Issue' }}
                    </span>
                  </td>
                  <td>{{ e.reference || '—' }}</td>
                  <td>
                    <span v-if="e.type === 'receipt'">{{ e.supplier || '—' }}</span>
                    <span v-else>{{ e.office || '—' }}</span>
                  </td>
                  <td class="text-right font-weight-bold" :class="e.type === 'receipt' ? 'text-success' : 'text-danger'">
                    {{ e.type === 'receipt' ? '+' : '-' }}{{ n(Math.abs(e.quantity)) }}
                  </td>
                  <td class="text-right">{{ n(e.balance_after) }}</td>
                  <td class="text-muted small">{{ e.recorded_by || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>

    <!-- Edit modal -->
    <InvModal :open="showEdit" title="Edit Stock Card" @close="showEdit = false">
      <div class="form-group">
        <label>Description *</label>
        <input v-model="editForm.item_description" class="form-control" />
      </div>
      <div class="form-group">
        <label>Category</label>
        <select v-model="editForm.category" class="form-control">
          <option value="">None</option>
          <option v-for="c in supplyCategories" :key="c.id" :value="c.label || c.code">{{ c.label || c.code }}</option>
        </select>
      </div>
      <div class="form-row">
        <div class="form-group col">
          <label>Fund Cluster</label>
          <select v-model="editForm.fund_cluster" class="form-control">
            <option value="">None</option>
            <option v-for="fc in fundClusters" :key="fc.id" :value="fc.code">{{ fc.code }}</option>
          </select>
        </div>
        <div class="form-group col">
          <label>Unit of Measure *</label>
          <select v-model="editForm.unit_of_measure" class="form-control">
            <option v-for="u in units" :key="u.id" :value="u.code">{{ u.code }}</option>
          </select>
        </div>
      </div>
      <div class="form-row">
        <div class="form-group col">
          <label>Unit Cost</label>
          <input v-model.number="editForm.unit_cost" type="number" min="0" step="0.01" class="form-control" />
        </div>
        <div class="form-group col">
          <label>Reorder Point</label>
          <input v-model.number="editForm.reorder_point" type="number" min="0" class="form-control" />
        </div>
      </div>
      <div class="form-group d-flex align-items-center">
        <input id="is_active" type="checkbox" v-model="editForm.is_active" class="mr-2" />
        <label for="is_active" class="mb-0">Active (visible and usable)</label>
      </div>
      <template #footer>
        <button class="btn inv-btn-ghost mr-2" @click="showEdit = false">Cancel</button>
        <button class="btn inv-btn-primary" :disabled="saving" @click="submitEdit">Save changes</button>
      </template>
    </InvModal>

    <!-- Receive modal -->
    <InvModal :open="showReceive" title="Receive Stock" @close="showReceive = false">
      <p class="text-muted small mb-3" v-if="card">{{ card.stock_number }} — {{ card.item_description }}</p>
      <div class="form-group" style="position:relative">
        <label>Supplier</label>
        <input
          v-model="supplierSearch"
          class="form-control"
          placeholder="Search supplier…"
          autocomplete="off"
          @input="supplierOpen = true"
          @blur="hideSupplier"
          @focus="supplierOpen = true"
        />
        <div v-if="supplierOpen && filteredSuppliers.length" class="inv-supplier-dd">
          <div v-for="s in filteredSuppliers" :key="s.id" class="inv-supplier-opt" @mousedown.prevent="pickSupplier(s)">
            <strong>{{ s.name }}</strong>
            <span v-if="s.contact_person" class="text-muted small ml-1">· {{ s.contact_person }}</span>
          </div>
        </div>
      </div>
      <div class="form-row">
        <div class="form-group col">
          <label>Quantity *</label>
          <input v-model.number="receiveForm.quantity" type="number" min="0.01" step="0.01" class="form-control" />
        </div>
        <div class="form-group col">
          <label>Reference / PO No.</label>
          <input v-model="receiveForm.reference" class="form-control" />
        </div>
      </div>
      <template #footer>
        <button class="btn inv-btn-ghost mr-2" @click="showReceive = false">Cancel</button>
        <button class="btn inv-btn-primary" :disabled="saving" @click="submitReceive">Receive</button>
      </template>
    </InvModal>

  </div>
</template>

<script>
import { mapState } from "vuex";
import InvModal from "./components/InvModal.vue";
import { showInventoryError } from "./utils/inventoryErrors";

export default {
  name: "StockCardDetail",
  components: { InvModal },
  data() {
    return {
      card: null,
      loading: true,
      loadError: "",
      filter: "all",
      showEdit: false,
      showReceive: false,
      saving: false,
      editForm: {},
      receiveForm: { quantity: null, reference: "" },
      supplierSearch: "",
      selectedSupplier: null,
      supplierOpen: false,
    };
  },
  computed: {
    ...mapState("inventory", ["fundClusters", "categories", "units", "suppliers"]),
    activeCategories() {
      return this.categories.filter((c) => c.is_active);
    },
    supplyCategories() {
      return this.activeCategories.filter(
        (c) => !c.kind || ["office_supplies", "other_supplies", "accountable_forms"].includes(c.kind)
      );
    },
    filteredEntries() {
      if (!this.card) return [];
      const entries = [...(this.card.entries || [])].reverse();
      if (this.filter === "all") return entries;
      return entries.filter((e) => e.type === this.filter);
    },
    totalReceived() {
      return (this.card?.entries || [])
        .filter((e) => e.type === "receipt")
        .reduce((s, e) => s + Math.abs(Number(e.quantity)), 0);
    },
    totalIssued() {
      return (this.card?.entries || [])
        .filter((e) => e.type !== "receipt")
        .reduce((s, e) => s + Math.abs(Number(e.quantity)), 0);
    },
    receiptCount() {
      return (this.card?.entries || []).filter((e) => e.type === "receipt").length;
    },
    issueCount() {
      return (this.card?.entries || []).filter((e) => e.type !== "receipt").length;
    },
    filteredSuppliers() {
      const q = (this.supplierSearch || "").toLowerCase();
      if (!q) return this.suppliers;
      return this.suppliers.filter((s) =>
        s.name.toLowerCase().includes(q) || (s.contact_person || "").toLowerCase().includes(q)
      );
    },
  },
  methods: {
    n(v) {
      return Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    peso(v) { return "₱" + this.n(v); },
    fmtDate(d) {
      if (!d) return "—";
      return new Date(d).toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "numeric" });
    },
    load() {
      this.loading = true;
      this.loadError = "";
      this.$store.dispatch("inventory/fetchStockCard", this.$route.params.id)
        .then((card) => { this.card = card; })
        .catch((e) => {
          this.card = null;
          this.loadError = showInventoryError(e, "Error", "Failed to load stock card.");
        })
        .finally(() => { this.loading = false; });
    },
    openEdit() {
      this.editForm = {
        item_description: this.card.item_description,
        fund_cluster: this.card.fund_cluster || "",
        unit_of_measure: this.card.unit_of_measure,
        unit_cost: this.card.unit_cost,
        reorder_point: this.card.reorder_point,
        category: this.card.category || "",
        is_active: this.card.is_active !== false,
      };
      this.showEdit = true;
    },
    submitEdit() {
      this.saving = true;
      this.$store.dispatch("inventory/updateStockCard", { id: this.card.id, ...this.editForm })
        .then(() => {
          toastr.success("Stock card updated.", "Success");
          this.showEdit = false;
          this.load();
        })
        .catch((e) => showInventoryError(e, "Error"))
        .finally(() => { this.saving = false; });
    },
    openReceive() {
      this.receiveForm = { quantity: null, reference: "" };
      this.supplierSearch = "";
      this.selectedSupplier = null;
      this.showReceive = true;
    },
    pickSupplier(s) {
      this.selectedSupplier = s;
      this.supplierSearch = s.name;
      this.supplierOpen = false;
    },
    hideSupplier() {
      setTimeout(() => { this.supplierOpen = false; }, 150);
    },
    submitReceive() {
      if (!this.receiveForm.quantity) return toastr.warning("Enter a quantity.", "Required");
      this.saving = true;
      this.$store.dispatch("inventory/receiveStock", {
        id: this.card.id,
        quantity: this.receiveForm.quantity,
        reference: this.receiveForm.reference || null,
        supplier: this.selectedSupplier ? this.selectedSupplier.name : (this.supplierSearch || null),
      })
        .then(() => {
          toastr.success("Stock received.", "Success");
          this.showReceive = false;
          this.load();
        })
        .catch((e) => showInventoryError(e, "Error"))
        .finally(() => { this.saving = false; });
    },
    printCard() {
      const card = this.card;
      const entries = card.entries || [];
      const groups = {};
      entries.forEach((e) => {
        const d = new Date(e.transaction_date);
        const key = d.toLocaleString("en-PH", { month: "long", year: "numeric" });
        if (!groups[key]) groups[key] = [];
        groups[key].push(e);
      });
      const fmtD = (d) => { const dt = new Date(d); return (dt.getMonth()+1)+"/"+dt.getDate()+"/"+dt.getFullYear(); };
      const fmtN = (v) => { const n = Math.abs(Number(v||0)); return n%1===0?String(n):n.toFixed(2); };
      const ref = (e) => e.type==="receipt" ? [e.supplier,e.reference].filter(Boolean).join(" – ") : (e.reference||"");
      const openBal = (g) => {
        if (!g.length) return "";
        const f=g[0], qty=Math.abs(Number(f.quantity||0)), bal=Number(f.balance_after||0);
        return fmtN(f.type==="receipt"?bal-qty:bal+qty);
      };
      let rows = "";
      Object.keys(groups).forEach((month) => {
        const g = groups[month];
        rows += `<tr class="mr"><td colspan="2" style="padding-left:4px">${month}</td><td></td><td></td><td></td><td style="text-align:center">${openBal(g)}</td><td></td></tr>`;
        g.forEach((e) => {
          rows += `<tr>
            <td style="text-align:center">${fmtD(e.transaction_date)}</td>
            <td style="padding-left:4px">${ref(e)}</td>
            <td style="text-align:center">${e.type==="receipt"?fmtN(e.quantity):""}</td>
            <td style="text-align:center">${e.type!=="receipt"?fmtN(e.quantity):""}</td>
            <td style="padding-left:4px">${e.office||""}</td>
            <td style="text-align:center">${fmtN(e.balance_after)}</td>
            <td></td>
          </tr>`;
        });
      });
      for (let i=0;i<4;i++) rows+=`<tr style="height:18px"><td></td><td></td><td></td><td></td><td></td><td></td><td></td></tr>`;
      const html=`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Stock Card – ${card.stock_number}</title>
<style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:"Times New Roman",serif;font-size:12pt;color:#000}.page{width:210mm;padding:16mm 16mm 14mm;margin:0 auto}.appendix{text-align:right;font-style:italic;font-size:11pt;margin-bottom:3mm}.title{text-align:center;font-size:14pt;font-weight:bold;margin-bottom:6mm}.hdr{width:100%;border-collapse:collapse;font-size:11pt;margin-bottom:4mm}.hdr td{padding:1px 3px 3px;vertical-align:bottom;white-space:nowrap}.lbl{font-weight:bold}.ul{border-bottom:1px solid #000}.fw{font-weight:bold}table.lg{width:100%;border-collapse:collapse;font-size:10pt}table.lg th,table.lg td{border:1px solid #000;padding:2px 4px;vertical-align:middle}table.lg th{text-align:center}.cd{width:72px}.cq{width:52px;text-align:center}.cday{width:74px;text-align:center}.co{width:90px}.mr td{font-size:10pt}.np{text-align:center;padding:10px}@media print{@page{size:A4 portrait;margin:12mm 14mm}.np{display:none}.page{padding:0;width:100%}}</style>
</head><body>
<div class="np"><button onclick="window.print()" style="padding:6px 20px;background:#4f46e5;color:#fff;border:none;border-radius:5px;cursor:pointer;font-size:13px;">🖨 Print</button><button onclick="window.close()" style="margin-left:8px;padding:6px 14px;border:1px solid #ccc;border-radius:5px;cursor:pointer;font-size:13px;">Close</button></div>
<div class="page"><div class="appendix"><em>Appendix 58</em></div><div class="title">STOCK CARD</div>
<table class="hdr">
<tr><td class="lbl">Entity Name :</td><td class="ul" style="width:42%">Commission on Higher Education – Caraga Region</td><td style="width:4%"></td><td class="lbl" style="width:13%">Fund Cluster</td><td class="ul fw" style="width:12%">${card.fund_cluster||""}</td></tr>
<tr><td class="lbl">Item :</td><td class="ul fw">${card.item_description}</td><td></td><td class="lbl">Stock No. :</td><td class="ul">${card.stock_number}</td></tr>
<tr><td class="lbl">Description :</td><td class="ul fw">${card.item_description}</td><td></td><td class="lbl">Re-order Point :</td><td class="ul">${card.reorder_point>0?card.reorder_point:""}</td></tr>
<tr><td class="lbl">Unit of Measurement :</td><td class="ul" colspan="4">${card.unit_of_measure}</td></tr>
</table>
<table class="lg">
<thead><tr><th rowspan="2" class="cd">Date</th><th rowspan="2">Reference</th><th class="cq"><em>Receipt</em></th><th colspan="2"><em>Issue</em></th><th class="cq"><em>Balance</em></th><th rowspan="2" class="cday">No. of Days to<br>Consume</th></tr>
<tr><th class="cq">Qty.</th><th class="cq">Qty.</th><th class="co">Office</th><th class="cq">Qty.</th></tr></thead>
<tbody>${rows}</tbody></table></div>
<script>window.onload=function(){window.print();}<\/script></body></html>`;
      const win = window.open("","_blank","width=900,height=700,scrollbars=yes");
      win.document.write(html);
      win.document.close();
    },
  },
  mounted() {
    this.$store.dispatch("inventory/fetchFundClusters", { active_only: 1 });
    this.$store.dispatch("inventory/fetchCategories", { active_only: 1 });
    this.$store.dispatch("inventory/fetchUnits", { active_only: 1 });
    this.$store.dispatch("inventory/fetchSuppliers", { active_only: 1 });
    this.load();
  },
};
</script>
